import { useState, useEffect } from 'react'
import { useAppStore } from '@/store/appStore'
import { ASSESSMENT_FIELDS, ASSESSMENT_LABELS } from '@/lib/assessment-engine'
import type { WatchlistColumnType } from '@/types'

/**
 * Watchlist Configuration / Admin page.
 * Manage all watchlist columns: rename, change type, set options, hide/show, mark as filter, delete, add new.
 * 
 * Assessment column overrides (type, options, name, isFilter) stored in localStorage:
 *   tradingApp_assessmentColConfig: { [fieldId]: { type, options, name, isFilter } }
 * 
 * Custom column type/options stored in Supabase via API.
 * Filter columns stored in: tradingApp_watchlistFilterCols (string[])
 * Hidden columns stored in: tradingApp_hiddenWatchlistCols (string[])
 */

// Computed columns that cannot be renamed/edited/deleted
const COMPUTED_COLUMNS = ['ticker', 'name', 'currentPrice', 'sixMonthLow', 'fiftyTwoWeekHigh', 'sixMonthHigh', 'sixMonthDiscount']

const BASE_COLUMN_LABELS: Record<string, string> = {
  ticker: 'Ticker',
  name: 'Name',
  description: 'Description',
  currentPrice: 'Price',
  sixMonthLow: '6M Low',
  fiftyTwoWeekHigh: '52W High',
  sixMonthHigh: '6M High',
  sixMonthDiscount: '6M Discount %',
}

const COLUMN_TYPE_OPTIONS: { value: WatchlistColumnType; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'multiselect', label: 'Multi-select' },
]

// localStorage helpers for assessment column config
function getAssessmentConfig(): Record<string, { type?: WatchlistColumnType; options?: string[]; name?: string }> {
  try {
    return JSON.parse(localStorage.getItem('tradingApp_assessmentColConfig') || '{}')
  } catch { return {} }
}
function saveAssessmentConfig(config: Record<string, { type?: WatchlistColumnType; options?: string[]; name?: string }>) {
  localStorage.setItem('tradingApp_assessmentColConfig', JSON.stringify(config))
}

// localStorage helpers for filter columns
function getFilterColumns(): Set<string> {
  try {
    const stored = localStorage.getItem('tradingApp_watchlistFilterCols')
    return stored ? new Set(JSON.parse(stored)) : new Set(ASSESSMENT_FIELDS.filter(f => f !== 'notes' && f !== 'subCategory' && f !== 'aiStackRole'))
  } catch { return new Set() }
}
function saveFilterColumns(cols: Set<string>) {
  localStorage.setItem('tradingApp_watchlistFilterCols', JSON.stringify([...cols]))
}

export function WatchlistConfigPage() {
  const customColumns = useAppStore(s => s.customColumns)
  const fetchCustomColumns = useAppStore(s => s.fetchCustomColumns)
  const createCustomColumn = useAppStore(s => s.createCustomColumn)
  const updateCustomColumn = useAppStore(s => s.updateCustomColumn)
  const deleteCustomColumn = useAppStore(s => s.deleteCustomColumn)
  const categories = useAppStore(s => s.categories)
  const addCategory = useAppStore(s => s.addCategory)
  const removeCategory = useAppStore(s => s.removeCategory)
  const fetchWatchlistData = useAppStore(s => s.fetchWatchlistData)
  const stocks = useAppStore(s => s.stocks)

  const [assessmentConfig, setAssessmentConfig] = useState(getAssessmentConfig)
  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem('tradingApp_hiddenWatchlistCols')
      return stored ? new Set(JSON.parse(stored)) : new Set()
    } catch { return new Set() }
  })
  const [filterColumns, setFilterColumns] = useState<Set<string>>(getFilterColumns)

  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [renameDraft, setRenameDraft] = useState('')
  const [editingOptions, setEditingOptions] = useState<string | null>(null)
  const [optionsDraft, setOptionsDraft] = useState('')

  const [newName, setNewName] = useState('')
  const [newType, setNewType] = useState<WatchlistColumnType>('text')
  const [newOptions, setNewOptions] = useState('')
  const [newDescription, setNewDescription] = useState('')
  const [addError, setAddError] = useState('')
  const [adding, setAdding] = useState(false)

  // Category management state
  const [newCatName, setNewCatName] = useState('')
  const [renamingCatId, setRenamingCatId] = useState<string | null>(null)
  const [renameCatDraft, setRenameCatDraft] = useState('')

  useEffect(() => { fetchCustomColumns(); fetchWatchlistData() }, [fetchCustomColumns, fetchWatchlistData])

  // Persist helpers
  const saveHidden = (s: Set<string>) => { setHiddenColumns(s); localStorage.setItem('tradingApp_hiddenWatchlistCols', JSON.stringify([...s])) }
  const toggleHidden = (id: string) => { const n = new Set(hiddenColumns); if (n.has(id)) n.delete(id); else n.add(id); saveHidden(n) }
  const toggleFilter = (id: string) => { const n = new Set(filterColumns); if (n.has(id)) n.delete(id); else n.add(id); setFilterColumns(n); saveFilterColumns(n) }

  // Get effective type for a column
  const getColType = (colId: string, colCategory: 'computed' | 'assessment' | 'custom', customType?: WatchlistColumnType): WatchlistColumnType => {
    if (colCategory === 'custom') return customType || 'text'
    if (colCategory === 'assessment') return assessmentConfig[colId]?.type || 'text'
    return 'text'
  }

  // Get effective options for a column
  const getColOptions = (colId: string, colCategory: 'computed' | 'assessment' | 'custom', customOptions?: string[]): string[] => {
    if (colCategory === 'custom') return customOptions || []
    if (colCategory === 'assessment') return assessmentConfig[colId]?.options || []
    return []
  }

  // Handle type change
  const handleChangeType = async (colId: string, colCategory: string, newType: WatchlistColumnType) => {
    if (colCategory === 'custom') {
      await updateCustomColumn(colId, { type: newType })
    } else {
      const config = { ...assessmentConfig }
      config[colId] = { ...config[colId], type: newType }
      setAssessmentConfig(config)
      saveAssessmentConfig(config)
    }
  }

  // Handle options save
  const handleSaveOptions = async (colId: string, colCategory: string) => {
    const options = optionsDraft.split(',').map(o => o.trim()).filter(Boolean)
    if (colCategory === 'custom') {
      await updateCustomColumn(colId, { options })
    } else {
      const config = { ...assessmentConfig }
      config[colId] = { ...config[colId], options }
      setAssessmentConfig(config)
      saveAssessmentConfig(config)
    }
    setEditingOptions(null)
    setOptionsDraft('')
  }

  // Handle rename
  const handleRename = async (colId: string, colCategory: string, currentName: string) => {
    const trimmed = renameDraft.trim()
    if (!trimmed || trimmed === currentName) { setRenamingId(null); return }
    if (colCategory === 'custom') {
      await updateCustomColumn(colId, { name: trimmed })
    } else {
      const config = { ...assessmentConfig }
      config[colId] = { ...config[colId], name: trimmed }
      setAssessmentConfig(config)
      saveAssessmentConfig(config)
    }
    setRenamingId(null)
    setRenameDraft('')
  }

  const handleDelete = async (colId: string) => {
    if (!confirm('Delete this column? All values will be permanently lost.')) return
    await deleteCustomColumn(colId)
  }

  const handleAddColumn = async () => {
    const name = newName.trim()
    if (!name) { setAddError('Name is required'); return }
    setAdding(true)
    setAddError('')
    try {
      const options = (newType === 'dropdown' || newType === 'multiselect')
        ? newOptions.split(',').map(o => o.trim()).filter(Boolean) : []
      await createCustomColumn(name, newType, options, newDescription)
      setNewName(''); setNewType('text'); setNewOptions(''); setNewDescription('')
    } catch (e: any) {
      setAddError(e?.message || 'Failed to create column')
    } finally { setAdding(false) }
  }

  // Build column list
  type Row = { id: string; name: string; category: 'computed' | 'assessment' | 'custom'; columnType: WatchlistColumnType; options: string[]; editable: boolean; deletable: boolean }
  const allRows: Row[] = [
    ...COMPUTED_COLUMNS.map(id => ({ id, name: BASE_COLUMN_LABELS[id] || id, category: 'computed' as const, columnType: 'text' as WatchlistColumnType, options: [] as string[], editable: false, deletable: false })),
    { id: 'description', name: assessmentConfig['description']?.name || 'Description', category: 'assessment' as const, columnType: getColType('description', 'assessment'), options: getColOptions('description', 'assessment'), editable: true, deletable: false },
    ...ASSESSMENT_FIELDS.map(id => ({
      id,
      name: assessmentConfig[id]?.name || ASSESSMENT_LABELS[id] || id,
      category: 'assessment' as const,
      columnType: getColType(id, 'assessment'),
      options: getColOptions(id, 'assessment'),
      editable: true,
      deletable: false,
    })),
    ...customColumns.map(col => ({
      id: col.id, name: col.name, category: 'custom' as const,
      columnType: col.type, options: col.options, editable: true, deletable: true,
    })),
  ]

  // Column order management (reads/writes the same localStorage key as useColumnOrder hook)
  const COLUMN_ORDER_KEY = 'tradingApp_colOrder_watchlist'
  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(COLUMN_ORDER_KEY)
      if (stored) return JSON.parse(stored)
    } catch {}
    return allRows.map(r => r.id)
  })

  const moveColumnUp = (colId: string) => {
    const idx = columnOrder.indexOf(colId)
    if (idx <= 0) return
    const newOrder = [...columnOrder]
    ;[newOrder[idx - 1], newOrder[idx]] = [newOrder[idx], newOrder[idx - 1]]
    setColumnOrder(newOrder)
    localStorage.setItem(COLUMN_ORDER_KEY, JSON.stringify(newOrder))
  }

  const moveColumnDown = (colId: string) => {
    const idx = columnOrder.indexOf(colId)
    if (idx < 0 || idx >= columnOrder.length - 1) return
    const newOrder = [...columnOrder]
    ;[newOrder[idx], newOrder[idx + 1]] = [newOrder[idx + 1], newOrder[idx]]
    setColumnOrder(newOrder)
    localStorage.setItem(COLUMN_ORDER_KEY, JSON.stringify(newOrder))
  }

  // Sort allRows by columnOrder
  const sortedRows = [...allRows].sort((a, b) => {
    const ai = columnOrder.indexOf(a.id)
    const bi = columnOrder.indexOf(b.id)
    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi)
  })

  return (
    <div className="h-full overflow-y-auto px-6 py-6">
      <div className="max-w-5xl mx-auto">
        <h1 className="text-2xl font-bold text-[var(--color-text-primary)] mb-2">Watchlist Column Configuration</h1>
        <p className="text-sm text-[var(--color-text-muted)] mb-6">
          Manage all columns: rename, change type, set dropdown options, toggle as filter, hide/show, or delete.
        </p>

        {/* Column table */}
        <div className="border border-[var(--color-border)] rounded-lg overflow-hidden mb-8">
          <div className="grid grid-cols-[40px_1fr_110px_160px_60px_60px_70px] gap-2 px-4 py-3 bg-[var(--color-bg-tertiary)] text-xs font-semibold text-[var(--color-text-muted)] uppercase">
            <span className="text-center">Order</span>
            <span>Column Name</span>
            <span>Type</span>
            <span>Options</span>
            <span className="text-center">Filter</span>
            <span className="text-center">Visible</span>
            <span className="text-center">Actions</span>
          </div>

          {sortedRows.map((row, rowIndex) => {
            const effectiveType = row.columnType
            const effectiveOptions = row.options
            const isDropdownType = effectiveType === 'dropdown' || effectiveType === 'multiselect'

            return (
              <div
                key={row.id}
                className={`grid grid-cols-[40px_1fr_110px_160px_60px_60px_70px] gap-2 px-4 py-3 border-t border-[var(--color-border)] items-center ${
                  hiddenColumns.has(row.id) ? 'opacity-50 bg-[var(--color-bg-secondary)]' : ''
                }`}
              >
                {/* Order buttons */}
                <div className="flex flex-col items-center gap-0.5">
                  <button
                    onClick={() => moveColumnUp(row.id)}
                    disabled={rowIndex === 0}
                    className="text-xs text-[var(--color-text-muted)] hover:text-[var(--color-accent)] disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Move up"
                  >▲</button>
                  <button
                    onClick={() => moveColumnDown(row.id)}
                    disabled={rowIndex === sortedRows.length - 1}
                    className="text-xs text-[var(--color-text-muted)] hover:text-[var(--color-accent)] disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Move down"
                  >▼</button>
                </div>
                {/* Name */}
                <div className="flex items-center gap-2 min-w-0">
                  {renamingId === row.id ? (
                    <input
                      className="flex-1 px-2 py-1 text-sm border border-[var(--color-accent)] rounded outline-none text-[var(--color-text-primary)]"
                      value={renameDraft}
                      onChange={e => setRenameDraft(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') handleRename(row.id, row.category, row.name); if (e.key === 'Escape') setRenamingId(null) }}
                      onBlur={() => handleRename(row.id, row.category, row.name)}
                      autoFocus
                    />
                  ) : (
                    <span className="text-sm text-[var(--color-text-primary)] truncate">{row.name}</span>
                  )}
                  {row.category === 'computed' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-600 shrink-0">auto</span>}
                  {row.category === 'assessment' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-600 shrink-0">assessment</span>}
                  {row.category === 'custom' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-600 shrink-0">custom</span>}
                </div>

                {/* Type */}
                <div>
                  {row.category === 'computed' ? (
                    <span className="text-xs text-[var(--color-text-muted)]">Auto</span>
                  ) : (
                    <select
                      value={effectiveType}
                      onChange={e => handleChangeType(row.id, row.category, e.target.value as WatchlistColumnType)}
                      className="w-full px-2 py-1 text-xs border border-[var(--color-border)] rounded bg-white text-[var(--color-text-secondary)] focus:border-[var(--color-accent)] outline-none"
                    >
                      {COLUMN_TYPE_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Options */}
                <div className="min-w-0">
                  {row.category !== 'computed' && isDropdownType ? (
                    editingOptions === row.id ? (
                      <input
                        className="w-full px-2 py-1 text-xs border border-[var(--color-accent)] rounded outline-none"
                        value={optionsDraft}
                        placeholder="comma-separated"
                        onChange={e => setOptionsDraft(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleSaveOptions(row.id, row.category); if (e.key === 'Escape') setEditingOptions(null) }}
                        onBlur={() => handleSaveOptions(row.id, row.category)}
                        autoFocus
                      />
                    ) : (
                      <span
                        className="text-xs text-[var(--color-text-muted)] truncate block cursor-pointer hover:text-[var(--color-accent)]"
                        onClick={() => { setEditingOptions(row.id); setOptionsDraft(effectiveOptions.join(', ')) }}
                        title="Click to edit options"
                      >
                        {effectiveOptions.length > 0 ? effectiveOptions.join(', ') : '— click to set'}
                      </span>
                    )
                  ) : (
                    <span className="text-xs text-[var(--color-text-muted)]">—</span>
                  )}
                </div>

                {/* Filter toggle */}
                <div className="flex justify-center">
                  {row.category !== 'computed' && row.id !== 'description' && row.id !== 'notes' ? (
                    <button
                      onClick={() => toggleFilter(row.id)}
                      className={`w-8 h-5 rounded-full transition-colors relative ${
                        filterColumns.has(row.id) ? 'bg-orange-400' : 'bg-gray-300'
                      }`}
                      title={filterColumns.has(row.id) ? 'Is a filter — click to remove' : 'Not a filter — click to add'}
                    >
                      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                        filterColumns.has(row.id) ? 'left-3.5' : 'left-0.5'
                      }`} />
                    </button>
                  ) : (
                    <span className="text-xs text-[var(--color-text-muted)]">—</span>
                  )}
                </div>

                {/* Visible toggle */}
                <div className="flex justify-center">
                  {row.category !== 'computed' ? (
                    <button
                      onClick={() => toggleHidden(row.id)}
                      className={`w-8 h-5 rounded-full transition-colors relative ${
                        hiddenColumns.has(row.id) ? 'bg-gray-300' : 'bg-[var(--color-accent)]'
                      }`}
                      title={hiddenColumns.has(row.id) ? 'Hidden — click to show' : 'Visible — click to hide'}
                    >
                      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                        hiddenColumns.has(row.id) ? 'left-0.5' : 'left-3.5'
                      }`} />
                    </button>
                  ) : (
                    <span className="text-xs text-[var(--color-text-muted)]">—</span>
                  )}
                </div>

                {/* Actions */}
                <div className="flex justify-center gap-1">
                  {row.editable && (
                    <button onClick={() => { setRenamingId(row.id); setRenameDraft(row.name) }} className="p-1 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-accent)]" title="Rename">✏️</button>
                  )}
                  {row.deletable && (
                    <button onClick={() => handleDelete(row.id)} className="p-1 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-red)]" title="Delete">🗑️</button>
                  )}
                </div>
              </div>
            )
          })}
        </div>

        {/* Add new column */}
        <div className="border border-[var(--color-border)] rounded-lg p-6 bg-[var(--color-bg-secondary)]">
          <h2 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4">Add New Column</h2>
          <div className="flex gap-3 flex-wrap items-end">
            <div className="flex-1 min-w-[160px]">
              <label className="block text-xs font-medium text-[var(--color-text-muted)] mb-1">Name</label>
              <input type="text" value={newName} onChange={e => { setNewName(e.target.value); setAddError('') }} onKeyDown={e => e.key === 'Enter' && handleAddColumn()} placeholder="Column name" className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-lg text-[var(--color-text-primary)] focus:border-[var(--color-accent)] focus:ring-1 focus:ring-[var(--color-accent)] outline-none" />
            </div>
            <div className="min-w-[130px]">
              <label className="block text-xs font-medium text-[var(--color-text-muted)] mb-1">Type</label>
              <select value={newType} onChange={e => setNewType(e.target.value as WatchlistColumnType)} className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-lg text-[var(--color-text-secondary)] bg-white focus:border-[var(--color-accent)] outline-none">
                {COLUMN_TYPE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </div>
            {(newType === 'dropdown' || newType === 'multiselect') && (
              <div className="flex-1 min-w-[200px]">
                <label className="block text-xs font-medium text-[var(--color-text-muted)] mb-1">Options (comma-separated)</label>
                <input type="text" value={newOptions} onChange={e => setNewOptions(e.target.value)} placeholder="Option 1, Option 2" className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-lg text-[var(--color-text-primary)] focus:border-[var(--color-accent)] focus:ring-1 focus:ring-[var(--color-accent)] outline-none" />
              </div>
            )}
            <button onClick={handleAddColumn} disabled={adding} className="px-5 py-2 text-sm font-medium rounded-lg bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent)]/80 disabled:opacity-50 shrink-0">
              {adding ? '...' : '+ Add Column'}
            </button>
          </div>
          <div className="mt-3">
            <label className="block text-xs font-medium text-[var(--color-text-muted)] mb-1">Notes / Purpose (optional — included in exports)</label>
            <input type="text" value={newDescription} onChange={e => setNewDescription(e.target.value)} placeholder="What is this column for?" className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-lg text-[var(--color-text-primary)] focus:border-[var(--color-accent)] focus:ring-1 focus:ring-[var(--color-accent)] outline-none" />
          </div>
          {addError && <p className="mt-2 text-sm text-[var(--color-red)]">{addError}</p>}
        </div>

        <div className="mt-6 p-4 rounded-lg bg-blue-50 border border-blue-200">
          <p className="text-xs text-blue-700">
            <strong>Tips:</strong> "Auto" columns are computed from live data and can't be changed. Assessment and custom columns can be renamed, have their type changed to Dropdown (with options), hidden, or marked as a filter. Filter columns appear as dropdown filters at the top of the Watchlist page.
          </p>
        </div>

        {/* Categories Management */}
        <div className="mt-8 border border-[var(--color-border)] rounded-lg p-6 bg-[var(--color-bg-secondary)]">
          <h2 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4">Categories</h2>
          <p className="text-xs text-[var(--color-text-muted)] mb-4">
            Categories group stocks into collapsible sections on the Watchlist page. Rename, delete, or create new ones.
          </p>

          {categories.length === 0 ? (
            <p className="text-sm text-[var(--color-text-muted)] py-3">No categories yet.</p>
          ) : (
            <div className="space-y-2 mb-4">
              {categories.map(cat => {
                const stockCount = Object.values(stocks).filter(s => s.categoryId === cat.id).length
                return (
                  <div key={cat.id} className="flex items-center gap-3 p-3 rounded border border-[var(--color-border)] bg-white">
                    {renamingCatId === cat.id ? (
                      <input
                        className="flex-1 px-2 py-1 text-sm border border-[var(--color-accent)] rounded outline-none text-[var(--color-text-primary)]"
                        value={renameCatDraft}
                        onChange={e => setRenameCatDraft(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            // Save rename via localStorage override (categories come from Supabase stock data)
                            const trimmed = renameCatDraft.trim()
                            if (trimmed && trimmed !== cat.name) {
                              const overrides = JSON.parse(localStorage.getItem('tradingApp_categoryNameOverrides') || '{}')
                              overrides[cat.id] = trimmed
                              localStorage.setItem('tradingApp_categoryNameOverrides', JSON.stringify(overrides))
                            }
                            setRenamingCatId(null)
                          }
                          if (e.key === 'Escape') setRenamingCatId(null)
                        }}
                        onBlur={() => {
                          const trimmed = renameCatDraft.trim()
                          if (trimmed && trimmed !== cat.name) {
                            const overrides = JSON.parse(localStorage.getItem('tradingApp_categoryNameOverrides') || '{}')
                            overrides[cat.id] = trimmed
                            localStorage.setItem('tradingApp_categoryNameOverrides', JSON.stringify(overrides))
                          }
                          setRenamingCatId(null)
                        }}
                        autoFocus
                      />
                    ) : (
                      <span className="flex-1 text-sm text-[var(--color-text-primary)]">{cat.name}</span>
                    )}
                    <span className="text-xs text-[var(--color-text-muted)]">{stockCount} stocks</span>
                    <button
                      onClick={() => { setRenamingCatId(cat.id); setRenameCatDraft(cat.name) }}
                      className="p-1 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-accent)]"
                      title="Rename category"
                    >✏️</button>
                    <button
                      onClick={() => {
                        if (stockCount > 0) {
                          if (!confirm(`Delete "${cat.name}"? The ${stockCount} stocks in it will become uncategorized.`)) return
                        }
                        removeCategory(cat.id)
                      }}
                      className="p-1 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-red)]"
                      title="Delete category"
                    >🗑️</button>
                  </div>
                )
              })}
            </div>
          )}

          {/* Add new category */}
          <div className="flex gap-2">
            <input
              type="text"
              value={newCatName}
              onChange={e => setNewCatName(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && newCatName.trim()) {
                  addCategory(newCatName.trim())
                  setNewCatName('')
                }
              }}
              placeholder="New category name"
              className="flex-1 px-3 py-2 text-sm border border-[var(--color-border)] rounded-lg text-[var(--color-text-primary)] focus:border-[var(--color-accent)] focus:ring-1 focus:ring-[var(--color-accent)] outline-none"
            />
            <button
              onClick={() => {
                if (newCatName.trim()) {
                  addCategory(newCatName.trim())
                  setNewCatName('')
                }
              }}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent)]/80 shrink-0"
            >
              + Add
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default WatchlistConfigPage
